import { VOXEL_EMPTY } from "~/terrain/types";

const FloatArray =
  typeof Float16Array !== "undefined" ? Float16Array : Float32Array;

let voxelId = 1;
export function nextVoxelId() {
  return voxelId++;
}

function createBuffers(count: number, terrainTypeBuffer?: Uint8Array) {
  if (terrainTypeBuffer && count !== terrainTypeBuffer.length) {
    throw new Error("count must match terrainTypeBuffer length");
  }

  const uidBuffer = new Uint32Array(count);

  if (terrainTypeBuffer) {
    for (let i = 0; i < count; i++) {
      uidBuffer[i] = terrainTypeBuffer[i] === VOXEL_EMPTY ? 0 : nextVoxelId();
    }
  }

  return {
    uid: uidBuffer, // unique id of the voxel. [0,2^32)
    terrainType: terrainTypeBuffer || new Uint8Array(count), // the type of the voxel. [0,255]
    flags: new Uint8Array(count), // bitflags
    lastDestructedAt: new Uint32Array(count), // millis since game state
    destruction: new FloatArray(count), // [0,1]
    velocityX: new FloatArray(count),
    velocityY: new FloatArray(count), // voxels per tick
    tiredness: new Uint8Array(count), // [0,255]
  };
  // 1 + 1 + 4 + 4 + 4 + 4 = 20 bytes per voxel
  // each chunk is 64x64 voxels = 4096 voxels
  // each chunk is 4096 * 20 = 81920 bytes = 80kb
}

export class VoxelBuffer {
  public buffers: ReturnType<typeof createBuffers>;

  public count: number;

  constructor(terrainTypeBuffer: Uint8Array | number) {
    if (typeof terrainTypeBuffer === "number") {
      this.count = terrainTypeBuffer;
      this.buffers = createBuffers(terrainTypeBuffer);
    } else {
      this.count = terrainTypeBuffer.length;
      this.buffers = createBuffers(terrainTypeBuffer.length, terrainTypeBuffer);
    }
  }

  private index = 0;

  at(i: number) {
    this.index = i;
    return this;
  }

  resetModifiers() {
    this.buffers.flags.fill(0);
    this.buffers.lastDestructedAt.fill(0);
    this.buffers.destruction.fill(0);
  }

  resetVoxel(nextValue: number, modified: boolean) {
    this.buffers.uid[this.index] =
      nextValue === VOXEL_EMPTY ? 0 : nextVoxelId(); // reset id
    this.buffers.terrainType[this.index] = nextValue;
    this.buffers.flags[this.index] = modified ? this.FLAGS.modified : 0;
    this.buffers.lastDestructedAt[this.index] = 0;
    this.buffers.destruction[this.index] = 0;
    this.buffers.velocityX[this.index] = 0;
    this.buffers.velocityY[this.index] = 0;
    this.buffers.tiredness[this.index] = 0;
  }

  get uid(): number {
    return this.buffers.uid[this.index]!;
  }
  set uid(val: number) {
    this.buffers.uid[this.index] = val;
  }

  get tiredness(): number {
    return this.buffers.tiredness[this.index]!;
  }
  set tiredness(val: number) {
    this.buffers.tiredness[this.index] = val;
  }

  get terrainType(): number {
    return this.buffers.terrainType[this.index]!;
  }
  set terrainType(val: number) {
    this.buffers.terrainType[this.index] = val;
  }
  // setTerrainTypes(newValues: Uint8Array) {
  //   this.buffers.terrainType.set(newValues, 0);
  // }

  get destruction(): number {
    return this.buffers.destruction[this.index]!;
  }
  set destruction(val: number) {
    this.buffers.destruction[this.index] = val;
  }

  get velocityY(): number {
    return this.buffers.velocityY[this.index]!;
  }
  set velocityY(val: number) {
    this.buffers.velocityY[this.index] = val;
  }

  get velocityX(): number {
    return this.buffers.velocityX[this.index]!;
  }
  set velocityX(val: number) {
    this.buffers.velocityX[this.index] = val;
  }

  get lastDestructedAt(): number {
    return this.buffers.lastDestructedAt[this.index]!;
  }
  set lastDestructedAt(val: number) {
    this.buffers.lastDestructedAt[this.index] = val;
  }

  private FLAGS = {
    modified: 1 << 0,
    // next_thing: 1 << 1,
  };
  get modified(): boolean {
    return (this.buffers.flags[this.index]! & this.FLAGS.modified) !== 0;
  }
  set modified(val: boolean) {
    if (val) {
      this.buffers.flags[this.index]! |= this.FLAGS.modified;
    } else {
      this.buffers.flags[this.index]! &= ~this.FLAGS.modified;
    }
  }

  public dispose() {
    for (const key in this.buffers) {
      this.buffers[key as keyof typeof this.buffers] = null!;
    }
  }
}
